import React, { useState } from 'react';
import './JobApplicationForm.css';
import { Liferay } from '../common/services/liferay/liferay.js';
import api from '../common/services/liferay/api.js';
import DebugInfo from './DebugInfo.jsx';
import SuccessModal from './SuccessModal.jsx';

const JobApplicationForm = ({ourAgencyForceForm}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    surname: '',
    email: '',
    phoneNumber: '',
    jobAppliedFor: '',
    education: '',
    qualifications: '',
    cvFile: null,
    coverLetterFile: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing - only if error exists
    setErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    
    // Clear previous errors for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Validate file type and size
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Please upload a PDF or Word document'
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          [name]: 'File size must be less than 5MB'
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: file
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // First Name validation - Mandatory, alphabetic only, max 50 chars
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      newErrors.firstName = 'First Name must contain only alphabetic characters';
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = 'First Name must not exceed 50 characters';
    }

    // Middle Name validation - Optional, alphabetic only, max 50 chars
    if (formData.middleName.trim() && !/^[a-zA-Z\s]+$/.test(formData.middleName)) {
      newErrors.middleName = 'Middle Name must contain only alphabetic characters';
    } else if (formData.middleName.length > 50) {
      newErrors.middleName = 'Middle Name must not exceed 50 characters';
    }

    // Surname validation - Mandatory, alphabetic only, max 50 chars
    if (!formData.surname.trim()) {
      newErrors.surname = 'Surname is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.surname)) {
      newErrors.surname = 'Surname must contain only alphabetic characters';
    } else if (formData.surname.length > 50) {
      newErrors.surname = 'Surname must not exceed 50 characters';
    }

    // Email validation - Mandatory, email format validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Phone validation - Mandatory, 8-15 digits, allow country code
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required';
    } else {
      const cleanPhone = String(formData.phoneNumber).replace(/[^\d+]/g, '').trim();
      const phoneRegex = /^\+?\d{8,15}$/;
    if (!phoneRegex.test(cleanPhone)) {
      newErrors.phoneNumber = 'Phone number must be 8-15 digits (country code optional)';
    }
  }

    // Job Applied For validation - Pre-populated from job page context OR user selection
    if (!formData.jobAppliedFor.trim()) {
      newErrors.jobAppliedFor = 'Job Applied For is required';
    }

    // Education validation - Mandatory, max 100 chars
    if (!formData.education.trim()) {
      newErrors.education = 'Education is required';
    } else if (formData.education.length > 100) {
      newErrors.education = 'Education must not exceed 100 characters';
    }

    // Qualifications validation - Mandatory, max 250 chars
    if (!formData.qualifications.trim()) {
      newErrors.qualifications = 'Qualifications are required';
    } else if (formData.qualifications.length > 250) {
      newErrors.qualifications = 'Qualifications must not exceed 250 characters';
    }

    // CV file validation - Mandatory, PDF/DOC/DOCX only, max size 5MB
    if (!formData.cvFile) {
      newErrors.cvFile = 'CV is required';
    }

    // Cover Letter file validation - Optional, PDF/DOC/DOCX only, max size 5MB
    // No validation needed for optional field, file type/size validation handled in handleFileChange

    // Use functional update to ensure we're working with latest state
    setErrors(() => newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = validateForm();
    if (!isValid) {
      // Errors are set by validateForm, just return
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // Submit to Liferay API (you'll need to create a job application API endpoint)
      const result = await api.createJobApplication(formData, ourAgencyForceForm);
      
      // Reset form after successful submission
      setFormData({
        firstName: '',
        middleName: '',
        surname: '',
        email: '',
        phoneNumber: '',
        jobAppliedFor: '',
        education: '',
        qualifications: '',
        cvFile: null,
        coverLetterFile: null
      });
      
      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => {
        input.value = '';
      });
      
      // Show success modal
      setShowSuccessModal(true);
      
    } catch (error) {
      if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'test') {
        console.error('Error submitting job application:', error);
      }
      
      let errorMessage = 'Failed to submit job application. Please try again.';
      
      // Handle specific error cases
      if (error.message) {
        // Prioritize auth errors even if wrapped as "API Error (Status: 401)"
        if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.message.includes('API Error')) {
          // Keep default message for API errors
          errorMessage = 'Failed to submit job application. Please try again.';
        } else if (error.message.includes('400')) {
          errorMessage = 'Invalid data provided. Please check your input and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const getFileName = (file) => {
    return file ? file.name : '';
  };

  return (
    <div className="job-application-container">
      <DebugInfo />
      <div className="job-application-card">
        <h6 className="form-title regular text-primary-Blue-Tint-100">Apply For This Job</h6>        
        
        <form onSubmit={handleSubmit} className="job-application-form">
          <div className="form-group">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name"
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              disabled={isSubmitting}
              pattern="[a-zA-Z\s]+"
              title="First Name must contain only alphabetic characters, max 50 characters"
            />
            {errors.firstName && <span className="field-error">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <input
              type="text"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              placeholder="Middle Name"
              className={`form-input ${errors.middleName ? 'error' : ''}`}
              disabled={isSubmitting}
              maxLength={50}
              pattern="[a-zA-Z\s]*"
              title="Middle Name must contain only alphabetic characters, max 50 characters"
            />
            {errors.middleName && <span className="field-error">{errors.middleName}</span>}
          </div>

          <div className="form-group">
            <input
              type="text"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder="Surname"
              className={`form-input ${errors.surname ? 'error' : ''}`}
              disabled={isSubmitting}
              maxLength={50}
              pattern="[a-zA-Z\s]+"
              title="Surname must contain only alphabetic characters, max 50 characters"
            />
            {errors.surname && <span className="field-error">{errors.surname}</span>}
          </div>

          <div className="form-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              disabled={isSubmitting}
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Phone Number"
              className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
              disabled={isSubmitting}
              pattern="^\+?[0-9]{8,15}$"
              title="Phone number must be 8-15 digits (country code optional)"
            />
            {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
          </div>

          <div className="form-group">
            <input
              type="text"
              name="jobAppliedFor"
              value={formData.jobAppliedFor}
              onChange={handleChange}
              placeholder="Job Applied For"
              className={`form-input ${errors.jobAppliedFor ? 'error' : ''}`}
              disabled={isSubmitting}
            />
            {errors.jobAppliedFor && <span className="field-error">{errors.jobAppliedFor}</span>}
          </div>

          <div className="form-group">
            <textarea
              name="education"
              value={formData.education}
              onChange={handleChange}
              placeholder="Education"
              rows="1"
              className={`form-textarea ${errors.education ? 'error' : ''}`}
              disabled={isSubmitting}
              maxLength={100}
              title="Education must not exceed 100 characters"
            />
            {errors.education && <span className="field-error">{errors.education}</span>}
          </div>

          <div className="form-group">
            <textarea
              name="qualifications"
              value={formData.qualifications}
              onChange={handleChange}
              placeholder="Qualifications"
              rows="1"
              className={`form-textarea ${errors.qualifications ? 'error' : ''}`}
              disabled={isSubmitting}
              maxLength={250}
              title="Qualifications must not exceed 250 characters"
            />
            {errors.qualifications && <span className="field-error">{errors.qualifications}</span>}
          </div>

          <div className="form-group file-upload-group">
            <div className="file-upload-container">
              <input
                type="file"
                name="cvFile"
                id="cvFile"
                onChange={handleFileChange}
                className="file-input"
                disabled={isSubmitting}
              />
              <label htmlFor="cvFile" className="file-label">
                <span className="file-label-text">Attach CV</span>
                <span className="file-button">Choose file</span>
              </label>
              {formData.cvFile && (
                <span className="file-name">{getFileName(formData.cvFile)}</span>
              )}
            </div>
            {errors.cvFile && <span className="field-error">{errors.cvFile}</span>}
          </div>

          <div className="form-group file-upload-group">
            <div className="file-upload-container">
              <input
                type="file"
                name="coverLetterFile"
                id="coverLetterFile"
                onChange={handleFileChange}
                className="file-input"
                disabled={isSubmitting}
              />
               <label htmlFor="coverLetterFile" className="file-label">
                <span className="file-label-text">Attach Cover Letter</span>
                <span className="file-button">Choose file</span>
              </label>
              {formData.coverLetterFile && (
                <span className="file-name">{getFileName(formData.coverLetterFile)}</span>
              )}
            </div>
          </div>

          {errors.submit && (
            <div className="field-error">
              {errors.submit}
            </div>
          )}

          <button 
            type="button"
            onClick={handleSubmit}
            className="btn primary-button regular"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Apply Online'}
          </button>
        </form>
      </div>
      
      <SuccessModal 
        isOpen={showSuccessModal} 
        onClose={handleCloseModal}
        title="Job Application Submitted!"
         message= {
    ourAgencyForceForm
      ? "Thank you for applying to join our Agency Force. Our recruitment team will reach out with next steps."
      : "Your application has been submitted successfully. Our HR team will review your profile and get back to you soon."}
      />
    </div>
  );
};

export default JobApplicationForm;
